import {
  appNorseApiRequest,
  appNoOp,
  appRefreshTokenFromTimeoutModal,
  appRefreshTokenFromTimeoutModalSuccess,
  appStart,
  onError,
  appAbandonToken,
  appAbandonTokenSuccess,
  appReset,
  appGetTokenInfoSuccess,
  appGetTokenInfoFailure,
  appCreateTokenSuccess,
  AppActionTypes,
  showTokenModalBeforeTimeout,
  appGetTokenKeepAlive,
  appGetTokenKeepAliveSuccess,
  appShowCookieModal,
  appHandleGtmScript,
  appMailingListAddPerson,
  appMailingListAddPersonSuccess,
  appUserLocated,
  appUserLocateFailed,
  appApiError,
  showTokenModal,
  autoRefreshToken,
  appNorseApiRequestCompleted, loadDynamicPagesConfig, loadDynamicPagesConfigSuccess,
} from '@actions/app.action';
import {
  BookingActionTypes,
  bookingGetBooking,
  bookingRetrieveTokenError,
} from '@actions/booking.action';
import { userGetUser } from '@actions/user.action';
import { inject, Injectable } from '@angular/core';
import { BottomImageModalComponent } from '@components/modals/bottom-image-modal/bottom-image-modal.component';
import { TokenTimeoutWarningModalComponent } from '@components/modals/token-timeout-warning-modal/token-timeout-warning-modal.component';
import {
  AGENT_AUTHENTICATION_ERROR_CODE,
  CookieConsentOptions,
  CULTURE_GREAT_BRITAIN_LOWER,
  CULTURE_ROUTE_PARAM,
  DEFAULT_TOKEN_TIMEOUT_MINUTES,
  FALLBACK_CULTURE,
  LOCALE_KEY_EN_GB,
  MILLISECONDS_PER_MINUTE,
  MODAL_DIALOG_CENTER_LG,
  PAYMENT_GREATER_THAN_DUE_ERROR_CODE,
  TOKEN_TIMEOUT_ERROR_CODE,
  WARN_USER_TOKEN_TIMEOUT_MINUTES,
} from '@constants/app.constants';
import { BaseError, ErrorType } from '@models/app.models';
import { SessionPermissionType } from '@models/responses/token-response.model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import {
  selectCloudflareMeta,
  selectTokenAutRefreshCount,
  selectTokenExpirationMinutes,
} from '@selectors/app.selector';
import { selectBooking } from '@selectors/booking.selector';
import { selectAppCmsCopy } from '@selectors/cms.selector';
import { selectRouteParam } from '@selectors/router.selector';
import { selectLoggedInUser } from '@selectors/user.selector';
import { TokenService } from '@services/api-clients';
import { MailingService } from '@services/api-clients/mailing.service';
import { CookieConsentService } from '@services/cookie-consent/cookie-consent.service';
import { GtmEnvService } from '@services/gtm-env/gtm-env.service';
import { ModalService } from '@services/modal/modal.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators';
import { updateCultureCode } from '@actions/culture.action';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { getCultureCodeParts } from '@utils/data-utils';
import {
  cmsCopyLoaded,
  cmsDisruptionBannersClear,
  cmsDisruptionBannersLoad,
  cmsDisruptionBannersSuccess,
} from '@actions/cms.action';
import { CmsKeys } from '@constants/cms.constants';
import { AppCopy } from '@models/cms/app.model';
import { CookiePolicyModalComponent } from '../../../components/modals/cookie-policy-modal/cookie-policy-modal.component';
import { GeocodeService } from '@services/geocode/geocode.service';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { RudderStackService } from '@services/rudderstack/rudderstack.service';
import { BBoxService } from '@services/bbox/bbox.service';
import { HotjarService } from '@services/hotjar/hotjar.service';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { MultiAppService } from '@services/multiapp/multiapp.service';
import { growthbookFeatureLoaded } from '@actions/growthbook.action';
import { PlatformService } from '@services/platform/platform.service';
import { AuthService } from '@services/auth/auth.service';
import { CmsService } from '@services/cms/cms.service';
import { ApiResponse } from '@models/responses/api-response.model';
import { DisruptionBanner } from '@models/cms/disruption-banner.model';
import { PosthogService } from '@services/posthog/posthog.service';
import {DynamicPageConfig} from "@models/cms/dynamic-pages-umbraco/dynamic-pages-config.model";

@Injectable()
export class AppEffect {
  readonly gbf = GrowthBookFeatureKeys;
  cookieModal: BsModalRef<CookiePolicyModalComponent> | null = null;

  private gbs: GrowthBookService = inject(GrowthBookService);
  private rs: RudderStackService = inject(RudderStackService);
  private bbs: BBoxService = inject(BBoxService);
  private hjs: HotjarService = inject(HotjarService);
  private posthog: PosthogService = inject(PosthogService);

  constructor(
    private actions$: Actions,
    private tokenService: TokenService,
    private modalService: ModalService,
    private store: Store,
    private cookieConsentService: CookieConsentService,
    private gtmEnvService: GtmEnvService,
    private mailingService: MailingService,
    private bsLocaleService: BsLocaleService,
    private geolocationService: GeocodeService,
    private platformService: PlatformService,
    private multiAppService: MultiAppService,
    private authService: AuthService,
    private cmsService: CmsService,
  ) {}

  appStart$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        appStart,
        // if we fail to get token info, we create one, which
        // then triggers us to get the info again
        appCreateTokenSuccess,
        bookingRetrieveTokenError,
      ),
      mergeMap((action) =>
        this.tokenService.getTokenContext().pipe(
          map(({ data: info }) => {
            if (action.type === BookingActionTypes.retrieveTokenError) {
              return appGetTokenInfoSuccess({ info, next: action.next });
            }

            return appGetTokenInfoSuccess({ info });
          }),
          catchError(() => {
            // prevent more than one call after creating a token
            if (action.type === AppActionTypes.createTokenSuccess) {
              return of(appNoOp());
            }
            return of(appGetTokenInfoFailure());
          }),
        ),
      ),
    );
  });

  appStartGetPosition$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(appStart),
      filter(() => {
        const featureValue = this.gbs.growthBook.getFeatureValue(
          'feat-002341-cloudflare-geolocation',
          '',
        );
        return featureValue !== 'b';
      }),
      mergeMap(() => {
        return combineLatest([
          this.geolocationService.checkPermissions(),
          this.geolocationService.getCurrentPosition(),
        ]).pipe(
          switchMap(([permission, position]) => {
            if (!position) {
              return of(appUserLocateFailed());
            }
            return of(
              appUserLocated({
                lat: position?.coords.latitude,
                lon: position?.coords.longitude,
                byPrompt:
                  permission.state === 'granted' ||
                  permission.state === 'prompt',
              }),
            );
          }),
          catchError(() => {
            return of(appUserLocateFailed());
          }),
        );
      }),
    );
  });

  appStartGetPositionFromCloudflare$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(appStart),
      filter(() => {
        const featureValue = this.gbs.growthBook.getFeatureValue(
          'feat-002341-cloudflare-geolocation',
          '',
        );
        return featureValue === 'b';
      }),
      mergeMap(() => {
        return combineLatest([this.store.select(selectCloudflareMeta)]).pipe(
          switchMap(([cloudflareMeta]) => {
            if (!cloudflareMeta) {
              return of(appUserLocateFailed());
            }
            return of(
              appUserLocated({
                lat: cloudflareMeta.latitude,
                lon: cloudflareMeta.longitude,
                byPrompt: false,
              }),
            );
          }),
          catchError(() => {
            return of(appUserLocateFailed());
          }),
        );
      }),
    );
  });

  updateNgxBootstrapLocale$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(updateCultureCode),
        tap(({ code }) => {
          const localeKey =
            code?.toLowerCase() === CULTURE_GREAT_BRITAIN_LOWER
              ? LOCALE_KEY_EN_GB
              : getCultureCodeParts(code).language;
          this.bsLocaleService.use(localeKey);
        }),
      );
    },
    {
      dispatch: false,
    },
  );

  addToMailingList$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(appMailingListAddPerson),
      mergeMap(({ person }) => {
        return this.mailingService.addPerson(person).pipe(
          switchMap(() => {
            return of(
              appMailingListAddPersonSuccess({
                person,
              }),
            );
          }),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.MailingListAddPersonFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  showMailingListAddSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(appMailingListAddPersonSuccess),
        concatLatestFrom(() => this.store.select(selectAppCmsCopy)),
        tap(([, copy]) => {
          const footerCopy = copy?.footer;
          this.modalService.show(BottomImageModalComponent, {
            class: MODAL_DIALOG_CENTER_LG,
            initialState: {
              btnText: footerCopy?.okayCopy,
              text: footerCopy?.addedToMailingListCopy,
              img: 'https://assets.flynorse.com/static/site/modals/three-tails.jpg',
              altText: footerCopy?.modalImageAlt,
            },
          });
        }),
      );
    },
    {
      dispatch: false,
    },
  );

  appReset$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(appReset),
        tap(() => {
          this.modalService.hideAll();
        }),
      );
    },
    {
      dispatch: false,
    },
  );

  appKeepTokenAlive$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(appGetTokenKeepAlive),
      mergeMap(() =>
        this.tokenService.refreshToken().pipe(
          map(() => appGetTokenKeepAliveSuccess()),
          catchError(() => of(appAbandonToken())),
        ),
      ),
    );
  });

  appCreateToken$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(appGetTokenInfoFailure, appReset),
      concatLatestFrom(() => [this.store.select(selectTokenExpirationMinutes)]),
      mergeMap(([, expirationMinutes]) =>
        this.tokenService.getNewToken().pipe(
          switchMap(
            ({
              data: {
                idleTimeoutInMinutes: responseTimeoutInMinutes,
                token: responseToken,
              },
            }) => {
              this.authService.setToken(responseToken);
              // token refresh does not give us the timeout, so we must assume it starts
              // from the time of refresh, with the same timeout minutes as before (pulled from
              // state). token refresh returns a timeout in minutes of 0.
              const idleTimeoutInMinutes =
                responseTimeoutInMinutes <= 0
                  ? (expirationMinutes ?? DEFAULT_TOKEN_TIMEOUT_MINUTES)
                  : responseTimeoutInMinutes;
              return of(
                appCreateTokenSuccess({
                  idleTimeoutInMinutes,
                }),
              );
            },
          ),
        ),
      ),
    );
  });

  appGetLoggedInUser$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(appGetTokenInfoSuccess),
      mergeMap(({ info, next }) => {
        if (next) {
          return of(next);
        }
        if (
          info.type === SessionPermissionType.Anonymous ||
          info.type === SessionPermissionType.Unknown
        ) {
          return of(appNoOp());
        }
        return of(userGetUser());
      }),
    );
  });

  timeoutModalRef: BsModalRef<TokenTimeoutWarningModalComponent> | null = null;

  showTokenModalBeforeTimeout$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(showTokenModalBeforeTimeout),
      concatLatestFrom(() => [
        this.store.select(selectBooking),
        this.store.select(selectLoggedInUser),
      ]),
      mergeMap(([, booking, user]) => {
        // keep token alive indefinitely if there's no booking
        // in state yet and no user logged in.
        if (!booking && !user) {
          return of(appGetTokenKeepAlive());
        }

        return of(showTokenModal());
      }),
    );
  });

  showTokenModalAfterTimeout$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(showTokenModal),
        tap(() => {
          this.modalService.hideAll();

          this.timeoutModalRef = this.modalService.show(
            TokenTimeoutWarningModalComponent,
            {
              ignoreBackdropClick: true,
            },
          );
        }),
      );
    },
    {
      dispatch: false,
    },
  );

  // on every API call: triggers the token service to start a timeout, which will
  // dispatch an action that shows the token expiration modal before it expires
  // (based on a constant number of minutes of warning set in the app constants)
  appStartTokenTimeout$: Observable<unknown> = createEffect(() => {
    return this.actions$.pipe(
      ofType(appNorseApiRequest),
      concatLatestFrom(() => [
        this.store.select(selectTokenExpirationMinutes),
        this.store.select(selectTokenAutRefreshCount),
      ]),
      tap(([, timeoutMinutes, tokenAutoRefreshCount]) => {
        if (!timeoutMinutes) {
          console.warn(
            'Norse API request effect ignored. (Triggered before timeout in minutes was available)',
          );
          return;
        }
        const expirationMs =
          (timeoutMinutes - WARN_USER_TOKEN_TIMEOUT_MINUTES) *
          MILLISECONDS_PER_MINUTE;
        this.tokenService.delayDispatchShowExpirationTimeoutModal(
          expirationMs,
          tokenAutoRefreshCount,
        );
      }),
      map(() => appNoOp()),
    );
  });

  appRefreshToken$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(appRefreshTokenFromTimeoutModal),
      mergeMap(() => {
        return this.tokenService.refreshToken(true).pipe(
          switchMap(() => of(appRefreshTokenFromTimeoutModalSuccess())),
          catchError(() => of(appAbandonToken())),
        );
      }),
    );
  });

  appRefreshTokenSuccess$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(appRefreshTokenFromTimeoutModalSuccess),
        tap(() => {
          this.timeoutModalRef?.hide();
        }),
      );
    },
    { dispatch: false },
  );

  appAbandonToken$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(appAbandonToken),
      mergeMap(() => {
        return this.tokenService.abandonToken().pipe(
          switchMap(() => {
            this.authService.removeToken();
            return of(appAbandonTokenSuccess());
          }),
          // error in this case generally means token already expired
          // which we interpret as successfully abandoning the token
          catchError(() => {
            this.authService.removeToken();
            return of(appAbandonTokenSuccess());
          }),
        );
      }),
    );
  });

  appTokenAbandoned$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(appAbandonTokenSuccess),
      mergeMap(() =>
        of(
          appReset({
            width: this.platformService.width(),
            height: this.platformService.height(),
          }),
        ),
      ),
    );
  });

  onError$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(onError),
      map(({ error }) => {
        const { type } = error;
        switch (type) {
          case ErrorType.BookingCommitFailure:
          case ErrorType.BookingCompleteFailure:
          case ErrorType.BookingPollingFailure:
          case ErrorType.BookingRefundFailure:
            return bookingGetBooking({});
        }
        return appNoOp();
      }),
    );
  });

  onApiError$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(appApiError),
      mergeMap(({ errors }) => {
        if (
          errors.some(
            (err) =>
              err.code === TOKEN_TIMEOUT_ERROR_CODE ||
              err.code === AGENT_AUTHENTICATION_ERROR_CODE,
          )
        ) {
          // show timeout modal, token has timed out and
          // we didn't show the modal for some reason
          return of(showTokenModal());
        } else if (
          errors.some((err) => err.code === PAYMENT_GREATER_THAN_DUE_ERROR_CODE)
        ) {
          return of(bookingGetBooking({}));
        } else {
          return of(appNoOp());
        }
      }),
    );
  });

  appShowCookieConsentModal$: Observable<unknown> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(appShowCookieModal),
        concatLatestFrom(() => [
          this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)),
          this.store.select(selectAppCmsCopy),
        ]),
        tap(([, culture, appCopy]) => {
          import(
            '../../../components/modals/cookie-policy-modal/cookie-policy-modal.component'
          ).then(({ CookiePolicyModalComponent }) => {
            this.cookieModal = this.modalService.show(
              CookiePolicyModalComponent,
              {
                class: MODAL_DIALOG_CENTER_LG,
                ignoreBackdropClick: true,
                initialState: {
                  culture: culture ?? FALLBACK_CULTURE,
                  appCopy,
                },
              },
            );
          });
        }),
      );
    },
    { dispatch: false },
  );

  // update app copy for cookie modal in case modal was displayed before copy loaded
  appSetCookieConsentModalCopy$: Observable<unknown> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(cmsCopyLoaded),
        tap((loadedAction) => {
          if (!this.cookieModal?.content || loadedAction.key !== CmsKeys.App) {
            return;
          }
          this.cookieModal.content.appCopy = loadedAction.data as AppCopy;
        }),
      );
    },
    { dispatch: false },
  );

  appHandleGtmScript$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(appHandleGtmScript),
        tap(() => {
          const consent = this.cookieConsentService.getConsentCookie();
          if (
            consent === CookieConsentOptions.ALL.toString() ||
            consent.includes(CookieConsentOptions.MARKETING)
          ) {
            this.gtmEnvService.injectGtmScript();
          } else {
            this.gtmEnvService.removeGtmScript();
          }
        }),
      );
    },
    { dispatch: false },
  );

  onTokenAutoRefresh$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(autoRefreshToken),
      mergeMap(() => of(appGetTokenKeepAlive())),
    );
  });

  appNorseApiRequestCompleted$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(appNorseApiRequestCompleted),
        tap(({ req }) => {
          if (req.urlWithParams.toLowerCase().includes('clear=true')) {
            this.multiAppService.noBookingInState();
          }
        }),
      );
    },
    { dispatch: false },
  );

  onAppAbandonTokenSuccess$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(appAbandonTokenSuccess),
        tap(() => {
          this.multiAppService.noBookingInState();
        }),
      );
    },
    { dispatch: false },
  );

  initPosthog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(growthbookFeatureLoaded),
        tap(() => this.posthog.initPosthog()),
      );
    },
    {
      dispatch: false,
    },
  );

  cmsDisruptionBannersLoaded$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(cmsDisruptionBannersLoad),
      mergeMap(({ cultureCode }) => {
        if (!cultureCode) {
          return of(cmsDisruptionBannersClear());
        }
        return this.cmsService.getDisruptionBanners(cultureCode).pipe(
          switchMap((res: ApiResponse<DisruptionBanner[]>) =>
            res.data && res.data.length > 0
              ? of(cmsDisruptionBannersSuccess({ banners: res.data }))
              : of(cmsDisruptionBannersClear()),
          ),
          catchError(() => of(cmsDisruptionBannersClear())),
        );
      }),
    );
  });

  loadDynamicPagesConfig$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadDynamicPagesConfig),
      switchMap(() => {
        return this.cmsService.getDynamicPagesConfig().pipe(
          switchMap((res: ApiResponse<DynamicPageConfig[]>) => {
              return res.data && res.data.length > 0
                ? of(loadDynamicPagesConfigSuccess({config: res.data}))
                : of(appNoOp())
            }
          ),
          catchError(() => of(appNoOp())),
        );
      }),
    );
  });

}
